import { useEffect, useMemo, useState } from "react";
import {
  formatSizeLabel,
  measureImageUrlBytes,
  measurePreviewRasterBytes,
} from "../../../utils/measureSourcePreviewBytes";

function watermarkFingerprint(watermark) {
  if (!watermark || typeof watermark !== "object") return "";
  try {
    return JSON.stringify({
      contentType: watermark.contentType,
      text: watermark.text,
      size: watermark.size,
      opacity: watermark.opacity,
      rotation: watermark.rotation,
      tile: watermark.tile,
      spacing: watermark.spacing,
      position: watermark.position,
      margin: watermark.margin,
      offsetX: watermark.offsetX,
      offsetY: watermark.offsetY,
      color: watermark.color,
      fontId: watermark.fontId,
      fontWeight: watermark.fontWeight,
      fontStyle: watermark.fontStyle,
      letterSpacing: watermark.letterSpacing,
      textScript: watermark.textScript,
      logoUrl: watermark.logoUrl,
      logoNaturalWidth: watermark.logoNaturalWidth,
      logoNaturalHeight: watermark.logoNaturalHeight,
      logoCropRect: watermark.logoCropRect,
      flushEdges: watermark.flushEdges,
    });
  } catch {
    return "";
  }
}

/**
 * Live Original / Preview footnote sizes.
 * Preview re-measures on URL or watermark setting changes (debounced).
 */
export function useLiveSourceCardStats({
  originalImageUrl = null,
  previewImageUrl = null,
  originalSizeLabel = null,
  watermark = null,
  enabled = true,
  debounceMs = 220,
} = {}) {
  const [originalMeasured, setOriginalMeasured] = useState(null);
  const [previewMeasured, setPreviewMeasured] = useState(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  const wmKey = useMemo(() => watermarkFingerprint(watermark), [watermark]);

  useEffect(() => {
    if (!enabled || originalSizeLabel || !originalImageUrl) {
      if (!originalImageUrl && !originalSizeLabel) setOriginalMeasured(null);
      return undefined;
    }
    let cancelled = false;
    measureImageUrlBytes(originalImageUrl).then((bytes) => {
      if (!cancelled) setOriginalMeasured(formatSizeLabel(bytes));
    });
    return () => {
      cancelled = true;
    };
  }, [enabled, originalImageUrl, originalSizeLabel]);

  useEffect(() => {
    if (!enabled) {
      setPreviewMeasured(null);
      setPreviewBusy(false);
      return undefined;
    }
    const url = previewImageUrl || originalImageUrl;
    if (!url) {
      setPreviewMeasured(null);
      setPreviewBusy(false);
      return undefined;
    }

    let cancelled = false;
    setPreviewBusy(true);
    const timer = setTimeout(() => {
      measurePreviewRasterBytes(url, {
        watermark: wmKey ? watermark : null,
      })
        .then((bytes) => {
          if (cancelled) return;
          setPreviewMeasured(
            formatSizeLabel(bytes, { approx: Boolean(wmKey) })
          );
        })
        .finally(() => {
          if (!cancelled) setPreviewBusy(false);
        });
    }, debounceMs);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // watermark read when wmKey changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, previewImageUrl, originalImageUrl, wmKey, debounceMs]);

  return {
    originalSizeFootnote: originalSizeLabel || originalMeasured,
    previewSizeFootnote: previewBusy
      ? previewMeasured || "…"
      : previewMeasured || originalSizeLabel || originalMeasured,
    previewSizePending: previewBusy,
  };
}
